import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import mineCraftApi from '../../api/mineCraftApi';
import minecraftUrls from "../../configs/mine_craft/minecraftUrls";
import { AddonInterface } from '../../store/mine_craft/addonsSlice';
import { UserInterface } from "../../store/mine_craft/usersSlice";
import { userEntity, usersEntity } from "./usersActions";

const processStrategy = (value: any) => {
  return {
    ...value,
    url: minecraftUrls.addonPath(value.id),
    image_url: minecraftUrls.remoteUrl(value.image_url),
    texture_url: minecraftUrls.remoteUrl(value.texture_url),
    zip_url: minecraftUrls.remoteUrl(value.zip_url),
    ico_url: minecraftUrls.remoteUrl(value.ico_url)
  };
}

export const addonsEntity = new schema.Entity(
  'addons',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

export const addonEntity = new schema.Entity(
  'addon',
  {
    user: userEntity
  },
  {
    processStrategy
  }
);

export const fetchAddonsCollection = async (url: string, params: object = {}) => {
  const response    = await mineCraftApi.get(url, { params })
  const normalized  = normalize<
    any,
    {
      users: { [key: string]: UserInterface };
      addons: { [key: string]: AddonInterface };
    }
  >(response.data.addons, [addonsEntity]);

  return {
    users: normalized.entities.users || {},
    addons: normalized.entities?.addons || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      user: { [key: string]: UserInterface };
      addon: AddonInterface;
    }
  >(response.data.addon, addonEntity);
  return normalized.entities;
}

export const fetchPreviewAddons = createAsyncThunk(
  'addons/fetchPreviewAddons',
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons', params)
);

export const fetchAddons = createAsyncThunk(
  'addons/fetchAddons',
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons', params)
);

export const fetchAddonsTrending = createAsyncThunk(
  'addons/fetchAddons',
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons/trending', params)
);

export const fetchAddonsPopular = createAsyncThunk(
  'addons/fetchAddons',
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons/popular', params)
);

export const fetchAddonsRecents = createAsyncThunk(
  'addons/fetchAddons',
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons/recents', params)
);

export const fetchAddonsUnpublished = createAsyncThunk(
  'addons/fetchAddons',
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons/unpublished', params)
);

export const fetchAddon = createAsyncThunk(
  'addons/fetchAddon',
  async (id: number | string) => {
    const response = await mineCraftApi.get(`/onesky/addons/${id}`);
    return normalizeInstance(response);
  }
);

export const updateAddon = createAsyncThunk(
  'addons/updateAddon',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/addons/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyAddon = createAsyncThunk(
  'addons/destroyAddon',
  async (id: number) => {
    const response = await mineCraftApi.delete(`/onesky/addons/${id}`);
    return response.data;
  }
);
